// Per-tenant ticket-model config + matchers.
//
// The .136 dashboard hardcoded a single venue's gender-split nightclub model
// (Early Arrival / General Admission, Female/Male, VIP, Comp, free RSVP) as
// keyword checks scattered across the classification (EventDashboard), the
// structure detection (helpers.detectTicketStructure), and the card render
// (EventCard). This module externalizes the venue-specific VOCABULARY +
// category metadata into one config so it can vary per tenant.
//
// LIV is config #1 — DEFAULT_TICKET_MODEL reproduces the hardcoded behavior
// EXACTLY (same keywords, same buckets), so LIV output is unchanged. A new
// venue supplies its own model (different vocabulary/categories) via the
// tenant's event_dashboard_ticket_model JSONB; the dashboard loads it through
// dashboardContext and falls back to this default.

export const DEFAULT_TICKET_MODEL = {
  id: 'liv-nightclub',
  label: 'LIV Nightclub · gender-split tiers',
  // Keyword vocabulary used by classification + detection. `early` is the
  // strict "early arrival" tier; `earlyLoose` is the bare "early" token the
  // source uses to (a) qualify RSVP early tickets and (b) EXCLUDE early tiers
  // from the General buckets.
  vocab: {
    early: ['early arrival'],
    earlyLoose: 'early',
    general: ['general admission'],
    rsvp: 'rsvp',
    vip: 'vip',
    comp: 'comp',
    female: ['female'],
    male: ['male', 'men'],
  },
  // Classic gender-split buckets — display metadata (labels + Tailwind colors)
  // mirroring EventCard's hardcoded classic layout. `free` marks RSVP/Comp.
  // ARRAY ORDER IS THE CARD ROW ORDER (via entryRowOrder): rsvp, early,
  // general, vip, comp — the card's original hardcoded layout. Categories may
  // also carry `match: []` — exact (lowercased) rate names explicitly assigned
  // to the category, which beat the keyword rules in classifyRate.
  categories: [
    { key: 'rsvpEarlyFemale', label: 'RSVP Female',   gender: 'female', entry: 'rsvp',    free: true,  color: { text: 'text-green-700 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20' } },
    { key: 'rsvpEarlyMale',  label: 'RSVP Male',      gender: 'male',   entry: 'rsvp',    free: true,  color: { text: 'text-green-700 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20' } },
    { key: 'earlyFemale',    label: 'Early Female',   gender: 'female', entry: 'early',   free: false, color: { text: 'text-pink-700 dark:text-pink-400', bg: 'bg-pink-50 dark:bg-pink-900/20' } },
    { key: 'earlyMale',      label: 'Early Male',     gender: 'male',   entry: 'early',   free: false, color: { text: 'text-blue-700 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-900/20' } },
    { key: 'generalFemale',  label: 'General Female', gender: 'female', entry: 'general', free: false, color: { text: 'text-pink-800 dark:text-pink-300', bg: 'bg-pink-100 dark:bg-pink-900/30' } },
    { key: 'generalMale',    label: 'General Male',   gender: 'male',   entry: 'general', free: false, color: { text: 'text-blue-800 dark:text-blue-300', bg: 'bg-blue-100 dark:bg-blue-900/30' } },
    { key: 'vip',            label: 'VIP',            gender: null,     entry: 'vip',     free: false, color: { text: 'text-purple-700 dark:text-purple-400', bg: 'bg-purple-100 dark:bg-purple-900/30' } },
    { key: 'comp',           label: 'Comp',           gender: null,     entry: 'comp',    free: true,  color: { text: 'text-gray-700 dark:text-gray-300', bg: 'bg-gray-100 dark:bg-gray-800' } },
  ],
};

// Card row order = first-appearance order of entry types in categories[].
// Drives both the dashboard card's classic grid and the editor preview, so
// drag-reordering categories in the config actually reorders the card.
// Default model order = [rsvp, early, general, vip, comp] — identical to the
// original hardcoded layout.
export function entryRowOrder(m = _model) {
  const seen = [];
  for (const c of m.categories) if (!seen.includes(c.entry)) seen.push(c.entry);
  return seen;
}

// The active model. Set per-tenant from the backend config on dashboard mount
// (setTicketModel); defaults to LIV until then.
let _model = DEFAULT_TICKET_MODEL;
export function setTicketModel(model) { _model = model && model.vocab ? model : DEFAULT_TICKET_MODEL; }
export function getTicketModel() { return _model; }

// --- matchers (operate on an already lowercased, tier-normalized name) ---
const has = (name, kw) => (Array.isArray(kw) ? kw.some((k) => name.includes(k)) : name.includes(kw));

// Female MUST be tested before male: "female" contains the substring "male".
export function genderOf(name, m = _model) {
  if (has(name, m.vocab.female)) return 'female';
  if (has(name, m.vocab.male)) return 'male';
  return null;
}
export const isEarlyArrival = (name, m = _model) => has(name, m.vocab.early);
export const isEarlyLoose = (name, m = _model) => name.includes(m.vocab.earlyLoose);
export const isGeneral = (name, m = _model) => has(name, m.vocab.general);
export const isRsvp = (name, m = _model) => has(name, m.vocab.rsvp);
export const isVip = (name, m = _model) => has(name, m.vocab.vip);
export const isComp = (name, m = _model) => has(name, m.vocab.comp);

// Raw keyword presence (NOT gender-resolved) — used by structure detection's
// has*/categorization. Note the source quirk these preserve: "female" contains
// the substring "male", so hasMaleKeyword('… female') is true, matching .136.
export const hasFemaleKeyword = (name, m = _model) => has(name, m.vocab.female);
export const hasMaleKeyword = (name, m = _model) => has(name, m.vocab.male);

// Explicit per-category assignment (set by drag-and-drop in the admin editor):
// returns the category key whose `match` list contains this exact (lowercased)
// name, or null. Beats the keyword rules.
export function matchOverrideKey(name, m = _model) {
  for (const c of m.categories) {
    if (Array.isArray(c.match) && c.match.includes(name)) return c.key;
  }
  return null;
}

// Classify a (normalized, lowercased) rate name into a classic bucket key, or
// null. Match overrides are checked FIRST; otherwise reproduces the source's
// exact else-if order/keywords.
export function classifyRate(name, m = _model) {
  const override = matchOverrideKey(name, m);
  if (override) return override;
  const g = genderOf(name, m);
  // ANY rsvp-keyword rate is an RSVP (free) bucket — not just "rsvp early".
  // Requiring the early keyword sent "RSVP General Admission - Female" into the
  // PAID generalFemale bucket, so free RSVP sales notified with phantom $
  // (bucket count × the paid GA price). The bucket keys keep their legacy
  // rsvpEarly* names; their labels are already the generic "RSVP Female/Male".
  if (isRsvp(name, m) && g === 'female') return 'rsvpEarlyFemale';
  if (isRsvp(name, m) && g === 'male') return 'rsvpEarlyMale';
  if (isEarlyArrival(name, m) && g === 'female') return 'earlyFemale';
  if (isEarlyArrival(name, m) && g === 'male') return 'earlyMale';
  if (isGeneral(name, m) && g === 'female' && !isEarlyLoose(name, m)) return 'generalFemale';
  if (isGeneral(name, m) && g === 'male' && !isEarlyLoose(name, m)) return 'generalMale';
  if (isVip(name, m)) return 'vip';
  return null;
}

// Label lookup for a bucket key (used by EventCard).
export function categoryLabel(key, m = _model) {
  return (m.categories.find((c) => c.key === key) || {}).label || key;
}

// Color lookup for a bucket key — { text, bg } Tailwind class strings.
export function categoryColor(key, m = _model) {
  return (m.categories.find((c) => c.key === key) || {}).color || { text: '', bg: '' };
}

// Curated color palette for the admin editor. MUST be a FIXED set whose class
// strings appear here in source — Tailwind's JIT only generates classes it sees
// at build time, so admins pick from these rather than typing arbitrary classes.
// The first 7 are the venue-default pairs (match EventCard exactly); the rest
// are extras for new venues. EventCard renders whatever pair a category stores.
export const COLOR_PALETTE = [
  { name: 'Pink (light)',  text: 'text-pink-700 dark:text-pink-400',     bg: 'bg-pink-50 dark:bg-pink-900/20' },
  { name: 'Pink (deep)',   text: 'text-pink-800 dark:text-pink-300',     bg: 'bg-pink-100 dark:bg-pink-900/30' },
  { name: 'Blue (light)',  text: 'text-blue-700 dark:text-blue-400',     bg: 'bg-blue-50 dark:bg-blue-900/20' },
  { name: 'Blue (deep)',   text: 'text-blue-800 dark:text-blue-300',     bg: 'bg-blue-100 dark:bg-blue-900/30' },
  { name: 'Green',         text: 'text-green-700 dark:text-green-400',   bg: 'bg-green-50 dark:bg-green-900/20' },
  { name: 'Purple',        text: 'text-purple-700 dark:text-purple-400', bg: 'bg-purple-100 dark:bg-purple-900/30' },
  { name: 'Gray',          text: 'text-gray-700 dark:text-gray-300',     bg: 'bg-gray-100 dark:bg-gray-800' },
  { name: 'Amber',         text: 'text-amber-700 dark:text-amber-300',   bg: 'bg-amber-50 dark:bg-amber-900/20' },
  { name: 'Red',           text: 'text-red-700 dark:text-red-400',       bg: 'bg-red-50 dark:bg-red-900/20' },
  { name: 'Teal',          text: 'text-teal-700 dark:text-teal-300',     bg: 'bg-teal-50 dark:bg-teal-900/20' },
  { name: 'Indigo',        text: 'text-indigo-700 dark:text-indigo-300', bg: 'bg-indigo-50 dark:bg-indigo-900/20' },
  { name: 'Orange',        text: 'text-orange-700 dark:text-orange-300', bg: 'bg-orange-50 dark:bg-orange-900/20' },
];

// Find which palette entry a category's color matches (for the editor dropdown).
export function paletteIndexOf(color) {
  if (!color) return -1;
  return COLOR_PALETTE.findIndex((p) => p.text === color.text && p.bg === color.bg);
}
